<template>
  <div class="nebEditableText">
    <div>
      <p class="nebEditableText-text"
        v-show="!showInput"
        @mouseenter="onTextMouseEnter"
        :style="textStyle"
      >
        {{ text }}
      </p>
    </div>

    <div class="nebEditableText-inputWrapper"
      v-show="showInput"
      @mouseleave="onInputMouseLeave"
      @keyup.enter="submitInput"
    >
      <neb-input v-model="inputText"
        width="fit"
        @focus="disableMouseLeaveHandler = true"
        @blur="onInputBlur"
        tabindex="0"
        :inputStyle="inputStyle"
        :placeholder="placeholder"
      />

      <div class="nebEditableText-action"
        v-for="(action, idx) in actions"
        :key="action.id"
        @click="onAction(action)"
        :tabindex="idx + 1"
      >
        <ntv-icon v-if="action.icon"
          :name="action.icon"
          use-fa
        />
        <span v-if="action.label"
          class="nebEditableText-action-control"
        >
          {{ action.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { isNull, includes } from 'lodash'

import NebInput from '../Input'
import NtvIcon from '@nui/Icon'

/**
 * @typedef {Object} Action
 * @property {String} id - action id. This is the value that will be passed in the `id` field of the event payload.
 * @property {String} [label] - Action label. This will be displayed as the action "button".
 * @property {String} [icon] - Action icon. Optional. If `label` is also provided, the icon will be placed to the
 * left of the label. Ifno labelis provided, the icon will be displayed alone.
 * @property {Boolean} disabled - Whether or not this option should be disabled. Disabled options will be
 * rendered "grayed out".
 */

/**
 * @group Controls
 * A component that renders text that, when hovered, allows a user to edit said  text.
 */
export default {
  name: 'EditableText',

  components: {
    NebInput,
    NtvIcon
  },

  props: {
    /**
     * @param {(String|Number)} [text=] - The text rendered by the component.
     */
    text: {
      type: [String, Number],
      default: ''
    },
    /**
     * @param {Action[]} actions - A list of action configuratiion objects. Actions are rendeered to the right off
     * the input.
     */
    actions: {
      type: Array[Object],
      default: () => []
    },
    /**
     * @param {Object} [textStyle={}] - A Vue CSS object. Styles are applied to the text NOT in the input.
     */
    textStyle: {
      type: Object,
      default: () => ({})
    },
    /**
     * @param {Object} [inputStyle={}] - A Vue CSS object. Styles are applied to the text in the input.
     */
    inputStyle: {
      type: Object,
      default: () => ({})
    },
    /**
     * @param {String} [initialState] - Whether the input should be open on initial render. `'closed'` or `'open'`.
     */
    initialState: {
      type: String,
      default: 'closed'
    },
    /**
     * @param {String} [placeholder=''] - Placeholder ext for the input.
     */
    placeholder: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    showInput: false,
    disableMouseLeaveHandler: false,
    inputText: '',
    timeoutId: null
  }),

  methods: {
    onTextMouseEnter () {
      this.inputText = this.text
      this.showInput = true
    },
    onInputMouseLeave () {
      if (this.disableMouseLeaveHandler) { return }

      this.inputText = ''
      this.showInput = false
    },
    onInputBlur (e) {
      const target = e.relatedTarget

      if (!isNull(target) && includes(target.classList, 'nebEditableText-action')) {
        // It's important that we re-focus the input element otherwise clicking outside of the input will not
        // fire another `blur` event.

        // This is a workaround for an intermitent bug in FF regarding calling `focus` in an `onblur` handler.
        // see: https://bugzilla.mozilla.org/show_bug.cgi?id=53579
        this.timeoutId = setTimeout(() => e.target.focus(), 100)
        return
      }

      this.inputText = ''
      this.disableMouseLeaveHandler = false
      this.showInput = false
    },
    submitInput () {
      this.$emit('submit', this.inputText)
      document.activeElement.blur()
    },
    onAction (action) {
      this.$emit('action', action)
    }
  },

  mounted () {
    switch (this.initialState) {
      case 'open':
        this.showInput = true
        this.disableMouseLeaveHandler = true
        break
      default: return
    }
  },

  beforeDestroy () {
    clearTimeout(this.timeoutId)
  }
}
</script>

<style scoped lang="scss">
.nebEditableText {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;

  &-text {
    font-size: 12px;
    line-height: 1;
    height: 34px;
    padding: 10px 16px;
    margin: 0;
  }

  &-inputWrapper {
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }

  &-action {
    padding-right: 0.5em;

    &:not(:last-child) {
      border-right: 1px solid black;
    }

    &:not(:first-child) {
      padding-left: 0.5em;
    }

    &:focus {
      outline: none;
    }

    &-control {
      font-size: 12px;
      font-weight: 600;
      font-style: normal;
      font-stretch: normal;
      line-height: normal;
      letter-spacing: normal;
      color: #74cfe7;
      cursor: pointer;
    }

    svg {
      cursor: pointer;
    }
  }
}
</style>
